"use client";
import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea";
import { Info } from "lucide-react";
import { File as FileIcon, X } from "lucide-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { useR2Upload } from "../lib/useR2Upload.js";
import { cn } from "@/lib/utils";

const projectTypes = [
  { value: "capstone", label: "Capstone", score: 250 },
  { value: "standard", label: "Standard", score: 125 },
  { value: "challenge", label: "Challenge", score: 25 },
];

const editProjectSchema = z.object({
  title: z.string().min(1, "Project title is required"),
  // order: z.enum().min(1, "Order is required").regex(/^\d+$/, "Order must be a number (e.g. 1, 2, 3)"),
  // githubUrl: z.string().url("Please enter a valid GitHub URL").optional().or(z.literal("")),
  type: z.enum(["capstone", "standard", "challenge"]).optional(),
  score: z.coerce
    .number()
    .min(1, { message: "Points must be greater than 0" })
    .optional(),
  passmark: z.coerce
    .number()
    .min(1, { message: "Passmark is required" })
    .max(100, { message: "Passmark cannot be more than 100" }),
  description: z.string().min(1, "Content is required"),
  rubric: z.instanceof(File).optional(), // Added rubric field
});

export function EditProjectDialog({
  projectId,
  initialData = {
    title: "",
    type: undefined,
    score: 0,
    passmark: 0,
    description: "",
    rubric_link: null,
  },
  triggerButtonProps = {},
  onProjectAdded,
  refetchTopics,
  courseName,
}) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getToken } = useAuth();
  const { isUploading: isRubricUploading, uploadFile: uploadRubric } =
    useR2Upload(); // Destructure isUploading and uploadFile
  const [rubricFile, setRubricFile] = useState(null);
  const [existingRubricLink, setExistingRubricLink] = useState(
    initialData.rubric_link || null,
  );

  // Markdown editor state
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);

  // track whether the last score value was auto-filled from a type
  const lastSelectedType = useRef(null);
  const isAutoFillRef = useRef(false);

  // normalize incoming initial values
  const normalizeInitialData = (data) => {
    return {
      title: data?.title || "",
      type: data?.type ? String(data.type).toLowerCase() : undefined,
      score: data?.score != null ? Number(data.score) : 0,
      passmark: data?.passmark != null ? Number(data.passmark) : 0,
      description: data?.description || "",
      rubric_link: data?.rubric_link || null,
      topic_id: data?.topic_id != null ? Number(data.topic_id) : undefined,
      rubric: undefined,
    };
  };
  // Markdown editor handlers
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    form.setValue("description", value);
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  const normalizedInitialData = normalizeInitialData(initialData);

  const form = useForm({
    resolver: zodResolver(editProjectSchema),
    defaultValues: normalizedInitialData,
  });

  // reset when dialog opens
  useEffect(() => {
    if (!open) return;

    const resetData = normalizeInitialData(initialData);
    form.reset(resetData);
    lastSelectedType.current = resetData.type || null;
    isAutoFillRef.current = false;
    setRubricFile(null);
    setExistingRubricLink(resetData.rubric_link || null);
    // If there's an initial rubric_link, we might want to display it here
  }, [open, initialData]);

  const watchedType = form.watch("type");

  // Auto-update points when type changes
  useEffect(() => {
    if (!watchedType) return;

    // Find the selected type and its score
    const selectedType = projectTypes.find(
      (type) => type.value === watchedType,
    );
    if (!selectedType) return;

    if (lastSelectedType.current !== watchedType) {
      form.setValue("score", selectedType.score, { shouldValidate: true });
      lastSelectedType.current = watchedType;
    }
  }, [watchedType, form]);

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      let rubricLink = existingRubricLink; // Keep existing link by default, will be overwritten if a new file is uploaded
      if (rubricFile) {
        const projectNameSlug = values.title.toLowerCase().replace(/\s+/g, "-");
        const fileExtension = rubricFile.name.split(".").pop();
        const newFileName = `rubric.${fileExtension}`;
        const rubricPath = `rubrics/projects/${projectNameSlug}/${newFileName}`;

        // perform upload and handle different possible return shapes
        const uploadResult = await uploadRubric(rubricFile, rubricPath);
        console.debug("Rubric upload result:", uploadResult);

        if (!uploadResult) {
          // Upload failed, stop submission
          setIsSubmitting(false);
          toast.error("Rubric upload failed. Submission aborted.");
          return;
        }

        // uploadResult may be a string URL or an object { url, path }
        if (typeof uploadResult === "string") {
          rubricLink = uploadResult;
        } else if (uploadResult.url) {
          rubricLink = uploadResult.url;
        } else if (uploadResult.path) {
          // best-effort: build a public URL if hook returned only a path
          const publicBase = (
            import.meta.env?.VITE_R2_PUBLIC_URL || ""
          ).replace(/\/+$/g, "");
          const bucket = (import.meta.env?.VITE_BUCKET_NAME || "").replace(
            /\/+$/g,
            "",
          );
          rubricLink =
            publicBase && bucket
              ? `${publicBase}/${uploadResult.path}`
              : uploadResult.path;
        } else {
          // unexpected shape — fall back to string conversion
          rubricLink = String(uploadResult);
        }

        // update local existing link to the newly uploaded link
        setExistingRubricLink(rubricLink);
      }

      const token = await getToken();
      if (!token) return;

      // Basic client-side validation to avoid sending invalid values
      const scoreVal = Number(values.score);
      const passmarkVal = Number(values.passmark);

      if (isNaN(scoreVal) || scoreVal < 1) {
        toast.error("Max Score must be a positive number.");
        setIsSubmitting(false);
        return;
      }

      if (isNaN(passmarkVal) || passmarkVal < 1 || passmarkVal > 100) {
        toast.error("Passmark must be between 1 and 100.");
        setIsSubmitting(false);
        return;
      }

      if (!projectId) {
        toast.error("Project ID is required for editing.");
        setIsSubmitting(false);
        return;
      }

      // Ensure we have the topic id (API expects `topic_id` in body)
      const topicIdVal =
        values.topic_id ??
        normalizedInitialData.topic_id ??
        initialData?.topic_id;

      if (!topicIdVal) {
        toast.error("Topic ID is required to update the project.");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        title: values.title,
        type: values.type || null,
        score: scoreVal,
        passmark: passmarkVal,
        description: values.description,
        rubric_link: rubricLink,
        topic_id: Number(topicIdVal),
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      console.debug("Edit project payload:", payload);
      const editProjectResponse = await axios.put(
        api_endpoint + `/project/update/${projectId}`,
        payload,
        reqConfig,
      );
      const projectData =
        editProjectResponse.data?.project ?? editProjectResponse.data;
      console.log("Updated Project: ", projectData);
      toast.success(`Updated project "${projectData.title}" successfully!`);
      setOpen(false);

      // Normalize the returned data
      const normalizedReturnData = normalizeInitialData(projectData);

      form.reset(normalizedReturnData);
      lastSelectedType.current = normalizedReturnData.type || null;
      isAutoFillRef.current = false;
      setRubricFile(null);

      if (onProjectAdded) onProjectAdded(projectData);
      if (refetchTopics) refetchTopics();
    } catch (error) {
      console.error("Error updating project:", error);
      console.error("Server response:", error?.response?.data);
      const serverMsg =
        error?.response?.data?.message ||
        (error?.response?.data && JSON.stringify(error.response.data)) ||
        error.message ||
        "Failed to update project. Please try again.";
      toast.error(serverMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset to initial values instead of clearing
    const resetData = normalizeInitialData(initialData);
    form.reset(resetData);
    lastSelectedType.current = resetData.type || null;
    isAutoFillRef.current = false;
    setRubricFile(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-(--neutral-300) text-(--neutral-900) bg-(--surface-panel) hover:bg-(--neutral-100)"
          {...triggerButtonProps}
        >
          {/* <Edit className="w-4 h-4 mr-2" /> */}
          Edit Project
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Edit Project
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-600)">
            Update the project details below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
            id="edit-project-form"
          >
            <div className="">
              <FormField
                control={form.control}
                name="title"
                className="w-full"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Project Title *</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Enter project title"
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* <FormField
                control={form.control}
                name="order"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Order
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="e.g. 1, 2, 3"
                        type="number"
                        min="1"
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              /> */}
            </div>

            {/* <FormField
              control={form.control}
              name="githubUrl"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>
                    GitHub URL
                    <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      {...field}
                      placeholder="https://github.com/username/repository"
                      type="url"
                      className={cn(
                        "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                    />
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            /> */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>
                        Project Type *
                      </FieldLabel>
                      <FieldContent>
                        <Select
                          value={field.value || ""}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn(
                              "w-full bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                            )}
                          >
                            <SelectValue
                              placeholder="Select project type"
                              className="w-full min-w-0 font-body text-(--neutral-900)"
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-(--surface-panel) border border-(--neutral-300)">
                            {projectTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex items-center justify-between w-full font-body">
                                  <span className="font-body">
                                    {type.label}
                                  </span>
                                  <span className="text-xs text-(--neutral-500) ml-2 font-body">
                                    {type.score} pts
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError errors={[]} />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="score"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Max Score *</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Enter points"
                        type="number"
                        min="1"
                        value={field.value || ""}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const numValue =
                            rawValue === "" ? "" : Number(rawValue);
                          field.onChange(numValue);
                          if (!isAutoFillRef.current) {
                            lastSelectedType.current = null;
                          }
                          isAutoFillRef.current = false;
                        }}
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                      <div className="flex justify-center text-center text-xs text-(--neutral-500) font-body">
                        Auto-filled based on project type, <br />
                        but can be manually adjusted
                      </div>
                    </FieldContent>
                  </Field>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="passmark"
              className="w-full"
              render={({ field, fieldState }) => {
                const value = field.value || 0;
                const fillPercent = Math.min(Math.max(value, 0), 100);

                return (
                  <Field>
                    <FieldLabel>Passmark (%) *</FieldLabel>
                    <FieldContent>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={value}
                          onChange={(e) => {
                            let val = Number(e.target.value);
                            if (isNaN(val)) val = 0;
                            if (val < 0) val = 0;
                            if (val > 100) val = 100;
                            field.onChange(val);
                          }}
                          className={cn(
                            "w-16 border border-(--neutral-300) rounded px-2 py-1 text-sm text-center bg-(--surface-panel) text-(--neutral-900)",
                            fieldState.error &&
                              "border-(--color-destructive)",
                          )}
                        />
                        <div className="flex-1">
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            className="w-full h-2 bg-(--neutral-200) rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${fillPercent}%, var(--neutral-200) ${fillPercent}%, var(--neutral-200) 100%)`,
                            }}
                          />
                        </div>
                      </div>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                );
              }}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState }) => (
                <Field>
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <FieldLabel>
                      Content *
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Markdown supported
                      </div>
                    </FieldLabel>
                    <Button
                      type="button"
                      onClick={handleOpenMarkdownEditor}
                      size=""
                      variant="outline"
                      className=""
                    >
                      Edit in Fullscreen
                    </Button>
                  </div>
                  <FieldContent>
                    <Textarea
                      {...field}
                      rows={8}
                      className={cn(
                        "min-h-50 resize-none bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus:ring-0 font-body mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                      placeholder="Enter project description and requirements using Markdown"
                    />
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: "This is required" }]
                          : []
                      }
                    />
                    <div className="text-xs text-(--neutral-500) mt-1">
                      Use Markdown syntax for formatting. Code blocks, links,
                      lists, and emphasis are supported.
                    </div>
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="rubric"
              render={({ field }) => (
                <Field>
                  <FieldLabel>
                    Rubric
                    <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    {rubricFile || existingRubricLink ? (
                      <div className="flex items-center justify-between p-2 border rounded-md bg-gray-50">
                        <div className="flex items-center gap-2">
                          <FileIcon className="w-4 h-4 text-gray-500" />
                          {rubricFile ? (
                            <span className="text-sm font-medium">
                              {rubricFile.name}
                            </span>
                          ) : existingRubricLink ? (
                            <a
                              href={existingRubricLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium underline text-(--neutral-700)"
                            >
                              {existingRubricLink.split("/").pop()}
                            </a>
                          ) : (
                            <span className="text-sm font-medium">
                              No rubric
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6"
                          onClick={() => {
                            setRubricFile(null);
                            setExistingRubricLink(null);
                            field.onChange(undefined);
                          }}
                          type="button"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.md"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          field.onChange(file);
                          setRubricFile(file);
                          setExistingRubricLink(null);
                        }}
                        className="font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1"
                      />
                    )}
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            />

            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <div className="flex items-start gap-3">
                                                    <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                                    <div className="text-sm text-blue-800">
                                                        <p className="font-medium mb-1">Project Types</p>
                                                        <ul className="space-y-1 text-xs">
                                                            <li><strong>Capstone (250 pts):</strong> Major comprehensive projects</li>
                                                            <li><strong>Standard (125 pts):</strong> Regular practice projects</li>
                                                            <li><strong>Challenge (25 pts):</strong> Quick coding challenges</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div> */}
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting || isRubricUploading} // Disable if rubric is uploading
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            form="edit-project-form"
            variant=""
            className="" // Disable if rubric is uploading
            disabled={isSubmitting || isRubricUploading}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="var(--neutral-50)" />
              </span>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
        {/* Markdown Editor Dialog - positioned outside DialogContent but inside Dialog */}
        <MarkdownEditorDialog
          open={markdownEditorOpen}
          initialValue={form.watch("description") || ""}
          onClose={handleMarkdownEditorClose}
          onSave={handleMarkdownSave}
          contentFolder={initialData.title?.toLowerCase().replace(/\s+/g, "-")}
          contentName={initialData.title}
        />
      </DialogContent>
    </Dialog>
  );
}
